import { asyncForEach, removeDups, invertNonDestructive, safeInteropRegister } from "../../api-polyfill/src/common/windowFunctions";
import { PromiseWrapper } from "../../api-polyfill/src/utils/pw";
export class StorageService {
    getAdapterForTopic(topic) {
        return this.adapterMap[topic] ?? this.defaultAdapter;
    }
    constructor(glue, logger) {
        this.glue = glue;
        this.logger = logger;
        this.adapterMap = {};
        this.defaultAdapter = null;
        this.user = "defaultUser";
        this.uuid = "Finsemble";
        this.pwMap = {};
        this.adapterToTopicStash = {};
    }
    async clearCache({ topic }) {
        return new Promise((resolve) => {
            const adapter = this.getAdapterForTopic(topic);
            if (!adapter) {
                const err = `StorageService: No adapter found for topic "${topic}"`;
                this.logger.warn(`storage - clearCache(): ${err}`);
                resolve({ err });
                return;
            }
            if (!adapter.clearCache) {
                resolve({ err: `clearCache() not implemented in storage adapter ${adapter.baseName}` });
                return;
            }
            adapter?.clearCache({ topic, key: "" }, (err, data) => {
                resolve({
                    err,
                    data
                });
            });
        });
    }
    async get({ key, topic }) {
        return new Promise((resolve) => {
            const adapter = this.getAdapterForTopic(topic);
            if (!adapter) {
                const err = `StorageService: No adapter found for topic "${topic}"`;
                this.logger.warn(`storage - get(): ${err}`);
                resolve({ err });
                return;
            }
            adapter?.get({ topic, key }, (err, data) => {
                resolve({
                    err,
                    data
                });
            });
        });
    }
    async keys({ topic, keyPrefix }) {
        return new Promise((resolve) => {
            const adapter = this.getAdapterForTopic(topic);
            if (!adapter) {
                const err = `StorageService: No adapter found for topic "${topic}"`;
                this.logger.warn(`storage - keys(): ${err}`);
                resolve({ err });
                return;
            }
            const params = {
                topic,
                key: ""
            };
            if (keyPrefix) {
                params.keyPrefix = keyPrefix;
            }
            if (!adapter.keys) {
                resolve({ err: `keys() not implemented in storage adapter ${adapter.baseName}` });
                return;
            }
            adapter?.keys(params, (err, data) => {
                resolve({
                    err,
                    data
                });
            });
        });
    }
    async remove({ key, topic }) {
        return new Promise((resolve) => {
            const adapter = this.getAdapterForTopic(topic);
            if (!adapter) {
                const err = `StorageService: No adapter found for topic "${topic}"`;
                this.logger.warn(`storage - remove(): ${err}`);
                resolve({ err });
                return;
            }
            adapter?.delete({ topic, key }, (err, data) => {
                resolve({
                    err,
                    data
                });
            });
        });
    }
    async save({ key, topic, value }) {
        return new Promise((resolve) => {
            const adapter = this.getAdapterForTopic(topic);
            if (!adapter) {
                const err = `StorageService: No adapter found for topic "${topic}"`;
                this.logger.warn(`storage - save(): ${err}`);
                resolve({ err });
                return;
            }
            adapter?.save({ topic, key, value }, (err, data) => {
                resolve({
                    err,
                    data
                });
            });
        });
    }
    async setUser({ user }) {
        this.user = user;
        this.logger.debug("storage - Storage service setUser() called directly (probably because auth completed)");
        const adapters = (this.defaultAdapter ? [this.defaultAdapter] : []).concat(Object.values(this.adapterMap));
        await asyncForEach(adapters, async (adapter) => {
            await adapter.setUser(user);
        });
        this.logger.debug("storage - Storage service setUser() finished");
        return { err: null };
    }
    adapterIsLoaded(name, adapter) {
        this.logger.info(`storage - Storage service adapter ${name} asynchronously loaded successfully`);
        const topics = this.adapterToTopicStash[name];
        topics?.forEach((topic) => {
            if (topic === "*") {
                this.defaultAdapter = adapter;
            }
            else {
                this.adapterMap[topic] = adapter;
            }
        });
        this.pwMap[name]?.resolve();
        this.pwMap[name] = true;
    }
    async loadAdapter(name, url) {
        return new Promise((resolve) => {
            this.logger.debug(`storage - Storage service loading adapter ${url}`);
            if (globalThis.window) {
                const script = document.createElement("script");
                script.onload = async () => {
                    this.logger.debug(`storage - Storage service adapter ${url} script tag loaded successfully, waiting for adapter to call "adapterReceiver.loaded()"`);
                    if (!this.pwMap[name]) {
                        const pw = new PromiseWrapper();
                        pw.setTimeout(15000, `Storage adapter ${name} did not call adapterReceiver.loaded() within 15 seconds`);
                        this.pwMap[name] = pw;
                        await pw.promise;
                    }
                    else {
                        this.logger.info(`storage - Storage service adapter ${name} synchronously loaded successfully`);
                    }
                    resolve();
                };
                script.type = "text/javascript";
                script.src = url;
                document.body.append(script);
            }
            else {
                try {
                    require(url);
                }
                catch (e) {
                    this.logger.error(e);
                }
                resolve();
            }
        });
    }
    async loadAdaptersFromConfig(manifest) {
        const config = manifest.finsemble?.servicesConfig?.storage;
        if (!config) {
            const error = "storage - No servicesConfig.storage entry found in config";
            this.logger.warn(error);
            return error;
        }
        const { defaultStorage = "IndexedDBAdapter", topicToDataStoreAdapters = {}, dataStoreAdapters = {} } = config;
        const topicPlusDefault = {
            ...topicToDataStoreAdapters,
            "*": defaultStorage
        };
        this.logger.debug("storage - Storage service initializing", topicPlusDefault);
        this.adapterToTopicStash = invertNonDestructive(topicPlusDefault);
        await asyncForEach(Object.entries(dataStoreAdapters), ([name, url]) => this.loadAdapter(name, url));
        const adapters = Object.values(this.adapterMap).concat(this.defaultAdapter ?? []).filter(removeDups);
        await asyncForEach(adapters, async (adapter) => {
            await adapter.setBaseName(this.uuid);
            await adapter.setUser(this.user);
        });
        return null;
    }
    async start({ uuid, manifest }) {
        globalThis.adapterReceiver = {
            loaded: (name, adapter) => {
                this.adapterIsLoaded(name, adapter);
            }
        };
        this.uuid = uuid;
        await this.loadAdaptersFromConfig(manifest);
        await this.glue.interop.register("FSBL.Clients.StorageClient.get", safeInteropRegister(async (params) => this.get(params), this.logger));
        await this.glue.interop.register("FSBL.Clients.StorageClient.remove", safeInteropRegister(async (params) => this.remove(params), this.logger));
        await this.glue.interop.register("FSBL.Clients.StorageClient.save", safeInteropRegister(async (params) => this.save(params), this.logger));
        await this.glue.interop.register("FSBL.Clients.StorageClient.clearCache", safeInteropRegister(async (params) => this.clearCache(params), this.logger));
        await this.glue.interop.register("FSBL.Clients.StorageClient.keys", safeInteropRegister(async (params) => this.keys(params), this.logger));
        await this.glue.interop.register("FSBL.Clients.StorageClient.setUser", safeInteropRegister(async (params) => this.setUser(params), this.logger));
    }
}
